// ── resources-columns: the Resources list table columns (Wave 8) ────────────
// Each row links to the resource detail (/resources/:id). The Category badge
// uses the FE taxonomy; the Scope shows global vs scoped; enum display is
// humanized; a missing optional field renders "—". Ids are STRINGS — passed
// VERBATIM into the detail link. This table shows resource METADATA only — file
// transfer lives on the detail (and is the 503 gate-closed honest state).

import { Badge, StatusDot } from "@vjal/ui";
import { Link } from "react-router-dom";

import type { CoachingResourceDto } from "../../services/coaching-dtos";
import {
  orDash,
  resourceCategoryLabels,
  resourceCategoryOf,
  resourceScopeLabel,
  resourceStatusLabel,
  type ResourceCategory
} from "./resources-helpers";

export type ResourceRow = CoachingResourceDto & Record<string, unknown>;

function categoryTone(category: ResourceCategory): "success" | "info" | "warning" {
  if (category === "documentation") return "info";
  if (category === "reference_videos") return "warning";
  return "success";
}

export function buildResourcesCollectionColumns() {
  return [
    {
      id: "title",
      header: "Resource",
      sortable: true,
      sortAccessor: (row: ResourceRow) => row.title ?? "",
      searchValue: (row: ResourceRow) => `${row.title ?? ""} ${row.description ?? ""} ${row.cohort_name ?? ""}`,
      priority: 1,
      cell: (row: ResourceRow) => (
        <Link
          to={`/resources/${encodeURIComponent(row.id)}`}
          className="font-medium text-link no-underline hover:underline"
          data-exact-set-name="Open resource">
          {orDash(row.title)}
        </Link>
      )
    },
    {
      id: "resource_type",
      header: "Category",
      sortable: true,
      sortAccessor: (row: ResourceRow) => resourceCategoryOf(row),
      priority: 2,
      cell: (row: ResourceRow) => {
        const category = resourceCategoryOf(row);
        return <Badge variant={categoryTone(category)}>{resourceCategoryLabels[category]}</Badge>;
      }
    },
    {
      id: "scope",
      header: "Scope",
      sortable: true,
      sortAccessor: (row: ResourceRow) => resourceScopeLabel(row),
      priority: 2,
      cell: (row: ResourceRow) => resourceScopeLabel(row)
    },
    {
      id: "status",
      header: "Status",
      sortable: true,
      sortAccessor: (row: ResourceRow) => row.status ?? "",
      priority: 2,
      cell: (row: ResourceRow) => <StatusDot tone="info" label={resourceStatusLabel(row.status)}  />
    }
  ];
}
